import { hasCoordinates, staticMapUrl } from '../utils/map';
import { field, asString, parseEnrichedData } from '../utils/enriched-data';
import placeholderUrl from './placeholder-url';

/**
 * Extracts the poster URL from an item.
 * Checks multiple sources in order:
 * 1. Direct thumbnailUrl field
 * 2. Enriched data (TMDB poster, artwork, cover, etc.)
 * 3. Static map for places with coordinates
 * 4. Placeholder image as fallback
 */
export default function posterUrl(item: unknown): string {
  const thumb = field(item, 'thumbnailUrl');
  if (typeof thumb === 'string' && thumb) return thumb;

  const data = parseEnrichedData(field(item, 'enrichedData'));
  const type = asString(field(item, 'type'));

  if (data) {
    // Places: prefer Google Maps photo, fall back to static map tile
    if (type === 'place') {
      const photoUrl = asString(data.photoUrl);
      if (photoUrl) return photoUrl;
      const location = data.location;
      if (hasCoordinates(location)) {
        return staticMapUrl(location.latitude, location.longitude);
      }
    }

    // TMDB images
    const imgPath = (data.posterPath ?? data.profilePath) as string | undefined;
    if (typeof imgPath === 'string' && imgPath) {
      return imgPath.startsWith('http')
        ? imgPath
        : `https://image.tmdb.org/t/p/w300${imgPath}`;
    }

    // Product/service images
    if (data.artworkUrl) return data.artworkUrl as string;
    if (data.coverUrl) return data.coverUrl as string;
    if (data.thumbnailUrl) return data.thumbnailUrl as string;

    // Background/fallback images
    if (data.backgroundImage) return data.backgroundImage as string;
    if (data.imageUrl) return data.imageUrl as string;
  }

  return placeholderUrl(item);
}
